import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, ChevronLeft, ChevronRight, Send, CheckCircle, AlertCircle } from 'lucide-react';

interface CatalogoItem {
  categoria: string;
  slug: string | null;
  sigla: string;
  nome: string;
  competitor_product_id: string;
}

interface SurveyListItem {
  id: string;
  title: string;
}

type Resposta = { presente: boolean; preco?: number };

/**
 * Pesquisa PDV oficial — sortimento + preço por categoria, no formato do
 * formulário impresso (navega por categoria → lista sigla+nome → presença
 * + preço). Chave de `answers` é o `slug` da categoria (não o nome exibido)
 * porque o backend (POST /pesquisa-pdv/responses) espelha os preços em
 * competitor_price_records fazendo o lookup slug→nome de categoria — ver
 * backend/src/routes/pesquisaPdv.ts e backend/src/config/catalogo.ts.
 *
 * Offline: nenhum código especial aqui — o interceptor de resposta em
 * lib/api.ts já enfileira POST/PUT/PATCH/DELETE no IndexedDB (offline-queue)
 * quando `!navigator.onLine`, devolvendo uma resposta de sucesso "fake"
 * (`_offline: true`). É o MESMO padrão usado por PriceCollection.tsx: a tela
 * só chama `api.post(...)` normalmente e trata sucesso normalmente.
 */
export default function PesquisaPdv({ storeId, visitId }: { storeId: string; visitId?: string }) {
  const [, setLocation] = useLocation();
  const [catIndex, setCatIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Record<string, Resposta>>>({});

  const catalogoQuery = useQuery<CatalogoItem[]>({
    queryKey: ['pesquisa-catalogo'],
    queryFn: async () => (await api.get('/pesquisa-pdv/catalogo')).data.data,
  });
  const surveysQuery = useQuery<SurveyListItem[]>({
    queryKey: ['survey-oficial'],
    queryFn: async () =>
      (await api.get('/surveys', { params: { type: 'SORTIMENT', status: 'active' } })).data.data,
  });
  const catalogo = catalogoQuery.data ?? [];
  const surveys = surveysQuery.data ?? [];
  const survey = surveys.find((s) => s.title.includes('Pesquisa Oficial'));

  const backTo = visitId ? `/visit/${storeId}` : '/pesquisa-pdv';
  const isLoading = catalogoQuery.isLoading || surveysQuery.isLoading;
  const isQueryError = catalogoQuery.isError || surveysQuery.isError;

  // Categorias únicas por slug (chave real de answers), preservando o nome
  // de exibição. Itens sem slug (categoria fora do espelho `catalogo.ts`)
  // são descartados da pesquisa — não têm como ser respondidos corretamente.
  const categorias = useMemo(() => {
    const porSlug = new Map<string, string>();
    for (const item of catalogo) {
      if (!item.slug) continue;
      if (!porSlug.has(item.slug)) porSlug.set(item.slug, item.categoria);
    }
    return [...porSlug.entries()].map(([slug, nome]) => ({ slug, nome }));
  }, [catalogo]);

  const catAtual = categorias[catIndex];
  const itens = catAtual ? catalogo.filter((i) => i.slug === catAtual.slug) : [];

  const setResposta = (sigla: string, r: Resposta) => {
    if (!catAtual) return;
    setAnswers((a) => ({
      ...a,
      [catAtual.slug]: { ...(a[catAtual.slug] || {}), [sigla]: r },
    }));
  };

  const hasAnyAnswer = Object.values(answers).some((cat) => Object.keys(cat).length > 0);

  const submit = useMutation({
    mutationFn: async () =>
      api.post('/pesquisa-pdv/responses', {
        survey_id: survey!.id,
        visit_id: visitId || undefined,
        store_id: storeId,
        answers,
      }),
    onSuccess: () => setLocation(visitId ? `/visit/${storeId}` : '/pesquisa-pdv'),
    onError: (error: any) => {
      alert(error.response?.data?.error?.message || error.message || 'Erro ao enviar pesquisa');
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-gray-500">Carregando catálogo…</p>
        </div>
      </div>
    );
  }

  // Estado de erro cobre tanto falha de rede/API (isQueryError) quanto o caso
  // de ambas as queries terem carregado com sucesso mas sem dado utilizável
  // (survey oficial não encontrada na lista de ativas, ou catálogo sem
  // categorias válidas) — sem isso a tela ficava presa em "Carregando…"
  // indefinidamente (achado de review, Task 6 fix pass 1).
  const notFound = !isQueryError && (!survey || !catAtual);
  if (isQueryError || notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3 p-4 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-gray-700 font-medium">
          {isQueryError
            ? 'Não foi possível carregar a pesquisa.'
            : 'Pesquisa oficial não encontrada. Fale com seu supervisor.'}
        </p>
        {isQueryError && (
          <button
            onClick={() => {
              catalogoQuery.refetch();
              surveysQuery.refetch();
            }}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium"
          >
            Tentar novamente
          </button>
        )}
        <button
          onClick={() => setLocation(backTo)}
          className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium"
        >
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b p-3 flex items-center gap-2 z-10">
        <button
          onClick={() => setLocation(backTo)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          aria-label="Voltar"
        >
          <ArrowLeft size={20} />
        </button>
        {/* Hierarquia invertida a pedido da Kelli (UID 500, 22/07): a categoria
            pesquisada é a informação de trabalho do promotor — fica grande e em
            destaque; o nome da pesquisa vira caption. */}
        <div className="min-w-0">
          <p className="text-xs text-gray-500">
            Pesquisa PDV Oficial · {catIndex + 1}/{categorias.length}
          </p>
          <h1 className="font-bold text-lg leading-tight text-primary truncate">
            {catAtual.nome}
          </h1>
        </div>
      </header>

      <main className="p-3 space-y-2">
        {itens.map((item) => {
          const r = answers[catAtual.slug]?.[item.sigla];
          return (
            <div key={item.sigla} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-mono text-xs bg-gray-100 rounded px-1">{item.sigla}</span>
                  <span className="ml-2 text-sm text-gray-900">{item.nome}</span>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setResposta(item.sigla, { presente: true })}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      r?.presente ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    Tem
                  </button>
                  <button
                    onClick={() => setResposta(item.sigla, { presente: false })}
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      r && !r.presente ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    Não tem
                  </button>
                </div>
              </div>
              {r?.presente && (
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  placeholder="Preço (R$)"
                  value={r.preco ?? ''}
                  onChange={(e) =>
                    setResposta(item.sigla, {
                      presente: true,
                      preco: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  className="mt-2 w-32 border border-gray-200 rounded-xl px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                />
              )}
            </div>
          );
        })}
      </main>

      <footer className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-3">
        {catIndex === categorias.length - 1 && !hasAnyAnswer && (
          <p className="text-xs text-amber-600 text-center mb-2">
            Responda ao menos um item antes de enviar.
          </p>
        )}
        <div className="flex justify-between">
          <button
            disabled={catIndex === 0}
            onClick={() => setCatIndex(catIndex - 1)}
            className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 disabled:opacity-40"
          >
            <ChevronLeft size={18} />
          </button>
          {catIndex < categorias.length - 1 ? (
            <button
              onClick={() => setCatIndex(catIndex + 1)}
              className="px-4 py-2 rounded-xl bg-primary text-white flex items-center gap-1 font-semibold"
            >
              Próxima <ChevronRight size={18} />
            </button>
          ) : (
            <button
              onClick={() => submit.mutate()}
              disabled={submit.isPending || !hasAnyAnswer}
              className="px-4 py-2 rounded-xl bg-green-600 text-white flex items-center gap-1 font-semibold disabled:opacity-50"
            >
              <Send size={16} /> Enviar
            </button>
          )}
        </div>
      </footer>

      {submit.isSuccess && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-20">
          <div className="bg-white rounded-xl p-6 text-center">
            <CheckCircle className="mx-auto text-green-600" size={40} />
            <p className="mt-2 text-gray-900">Pesquisa enviada!</p>
          </div>
        </div>
      )}
    </div>
  );
}
